/**
 * Synced from convex/lib/fieldTypes.ts (BET-106: closed field type system, BR-108: MCP import boundary).
 */

/**
 * Closed field type system — single source of truth for collection field types.
 * BET-106: Makes the collection contract self-enforcing.
 *
 * Adding a new field type requires updating:
 * 1. VALID_FIELD_TYPES + FIELD_TYPE_DEFAULTS + isValidValueForType (this file)
 * 2. validateEntryData switch in convex/lib/guards.ts
 * 3. fieldDefValidator in convex/agentKnowledge/_helpers.ts
 * 4. Run: node scripts/sync-convex-lib-to-mcp.mjs
 * 5. packages/mcp-server/src/lib/fieldTypes.ts (mirror — must stay in sync)
 */

export const VALID_FIELD_TYPES = [
	'string',
	'text',
	'rich-text',
	'number',
	'boolean',
	'select',
	'multi-select',
	'array',
	'json',
	'date',
	/** Convex Id<'people'> as string — BR-130 */
	'person',
] as const;

export type FieldType = (typeof VALID_FIELD_TYPES)[number];

/** Type-aware defaults for smart-capture and entry creation. */
export const FIELD_TYPE_DEFAULTS: Record<FieldType, unknown> = {
	'string': '',
	'text': '',
	'rich-text': '',
	'number': null,
	'boolean': false,
	'select': null,
	'multi-select': [],
	'array': [],
	'json': null,
	'date': null,
	'person': null,
};

export function isValidFieldType(type: string): type is FieldType {
	return (VALID_FIELD_TYPES as readonly string[]).includes(type);
}

export function isValidValueForType(value: unknown, type: FieldType): boolean {
	switch (type) {
		case 'string':
		case 'text':
		case 'rich-text':
			return typeof value === 'string';
		case 'number':
			return value === null || typeof value === 'number';
		case 'boolean':
			return typeof value === 'boolean';
		case 'select':
			return value === null || typeof value === 'string';
		case 'multi-select':
		case 'array':
			return Array.isArray(value);
		case 'json':
			return true;
		case 'date':
			return value === null || (typeof value === 'string' && /^\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])$/.test(value));
		case 'person':
			// WP-630 (TEN-3042): deliberately NOT tightened to an id-shape regex. This
			// function returns a plain boolean with no separate advisory/blocking channel —
			// unlike kernel/entries/guards.ts's validateEntryData (warn vs error split), a
			// tightened `false` here would be indistinguishable from "reject." The write
			// gate's runtime Step-1.5 (convex/lib/personPlaceholder.ts) is the actual
			// guarantee (resolves-or-creates before validation ever sees a write-set value);
			// this predicate stays permissive so it can never disagree with that outcome —
			// see the shaping doc's "preview and gate must never disagree" disposition.
			return value === null || typeof value === 'string';
	}
}
